'use client';

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Wrench,
  CalendarRange,
  UserCircle,
  Calculator,
  FileText,
  FolderOpen,
  ChevronDown,
  BookOpen,
  Copy,
  Wallet,
  FileCheck,
  DollarSign,
  BarChart3,
  Lock,
  TrendingUp,
  AlertCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  Building2,
  ScrollText,
  ClipboardList,
  LogOut,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/auth/AuthContext';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  submenu?: {
    group: string;
    description: string;
    items: { to: string; label: string; icon: typeof LayoutDashboard }[];
  }[];
}

// Visibles per a qualsevol rol.
const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Tauler', icon: LayoutDashboard },
  { to: '/compres', label: 'Compres', icon: ShoppingCart },
  { to: '/crm', label: 'CRM', icon: Users },
  { to: '/taller', label: 'Taller', icon: Wrench },
  { to: '/planificacio-general', label: 'Planificació General', icon: CalendarRange },
];

// Nomes visibles per al rol ADMIN — la resta de rols nomes veuen Tauler,
// Compres, CRM, Taller i Administració (adminNavItems, mes avall).
const adminOnlyNavItems: NavItem[] = [
  { to: '/rrhh', label: 'RRHH', icon: UserCircle },
  {
    to: '/comptabilitat',
    label: 'Comptabilitat',
    icon: Calculator,
    submenu: [
      {
        group: 'Operativa',
        description: 'Entrada i gestió diària de dades',
        items: [
          { to: '/comptabilitat/journals', label: 'Diaris', icon: BookOpen },
          { to: '/comptabilitat/entries', label: 'Assentaments', icon: FileText },
          { to: '/comptabilitat/templates', label: 'Plantilles', icon: Copy },
          { to: '/comptabilitat/cashflow', label: 'Tresoreria', icon: Wallet },
        ],
      },
      {
        group: 'Fiscal',
        description: 'Compliment tributari i Hisenda',
        items: [
          { to: '/comptabilitat/vat', label: "Llibre d'IVA", icon: FileText },
          { to: '/comptabilitat/reports', label: 'Models Fiscals', icon: TrendingUp },
        ],
      },
      {
        group: 'Analítica',
        description: 'Consulta i informació',
        items: [
          { to: '/comptabilitat/coa', label: 'Pla de Comptes', icon: Calculator },
          { to: '/comptabilitat/search', label: 'Cerca Avançada', icon: FileCheck },
          { to: '/comptabilitat/ledger', label: 'Major', icon: DollarSign },
          { to: '/comptabilitat/statements', label: 'Estats Financers', icon: BarChart3 },
        ],
      },
      {
        group: 'Supervisió',
        description: 'Auditoria, supervisió i configuració',
        items: [
          { to: '/comptabilitat/audit', label: 'Auditoria', icon: AlertCircle },
          { to: '/comptabilitat/config', label: 'Configuració', icon: Settings },
          { to: '/comptabilitat/closing', label: 'Tancament de Període', icon: Lock },
        ],
      },
    ],
  },
  { to: '/facturacio', label: 'Facturació', icon: FileText },
  { to: '/documents', label: 'Documents', icon: FolderOpen },
];

const adminNavItems = [
  { to: '/admin', label: 'Panel Admin', icon: Shield },
];

export function Sidebar() {
  const location = useLocation();
  const { expanded, setExpanded } = useSidebar();
  const { user, logout, switchCompany } = useAuth();
  const [userPopupOpen, setUserPopupOpen] = useState(false);
  const userSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userSectionRef.current && !userSectionRef.current.contains(e.target as Node)) {
        setUserPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const currentCompanyName = user?.companies?.find(c => c.id === user.companyId)?.name ?? '';
  const userInitials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // Nomes el rol ADMIN veu RRHH/Comptabilitat/Facturació/Documents — la resta
  // de rols nomes veuen Tauler, Compres, CRM, Taller i Administració.
  const isAdmin = (user?.role?.name ?? '').toUpperCase() === 'ADMIN';
  const visibleNavItems = isAdmin ? [...navItems, ...adminOnlyNavItems] : navItems;

  useEffect(() => {
    if (location.pathname.startsWith('/comptabilitat')) {
      setExpandedMenu('Comptabilitat');
    } else if (location.pathname.startsWith('/crm')) {
      setExpandedMenu('CRM');
    }
  }, [location.pathname]);

  const toggleSubmenu = (label: string) => {
    setExpandedMenu(expandedMenu === label ? null : label);
  };

  return (
    <aside
      className={cn(
        'bg-sidebar-bg text-sidebar-text flex flex-col fixed left-0 top-0 h-screen border-r border-sidebar-border transition-all duration-300 z-40',
        expanded ? 'w-64' : 'w-20'
      )}
    >
      {/* Header with collapse toggle */}
      <div className="px-4 py-4 border-b border-sidebar-border flex items-center justify-between bg-white">
        {expanded ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 text-white">
              RF
            </div>
            <div>
              <h1 className="text-sm font-semibold" style={{color: 'rgb(2, 8, 23)'}}>ROIG I FILLS A.</h1>
              <p className="text-xs text-gray-500">Gestió Integral</p>
            </div>
          </div>
        ) : (
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 mx-auto text-white">
            RF
          </div>
        )}
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            title="Colapsar"
          >
            <ChevronLeft size={20} style={{color: 'rgb(2, 8, 23)'}} />
          </button>
        )}
      </div>

      {/* Collapse button when collapsed */}
      {!expanded && (
        <div className="px-3 py-2">
          <button
            onClick={() => setExpanded(true)}
            className="w-full p-1 hover:bg-sidebar-hover rounded transition-colors"
            title="Expandir"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.to ||
              (item.to !== '/' && location.pathname.startsWith(item.to));
            const isExpanded = expandedMenu === item.label;
            const hasSubmenu = 'submenu' in item && item.submenu;

            return (
              <li key={item.to}>
                {hasSubmenu ? (
                  <button
                    onClick={() => {
                      if (expanded) {
                        toggleSubmenu(item.label);
                      }
                    }}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors text-xs',
                      isActive || isExpanded
                        ? 'text-sidebar-active-text bg-sidebar-active'
                        : 'text-sidebar-text hover:bg-sidebar-hover'
                    )}
                    style={isActive || isExpanded ? {
                      color: 'rgb(248, 250, 252)',
                      backgroundColor: 'rgb(15, 23, 42)'
                    } : {
                      color: 'rgb(2, 8, 23)'
                    }}
                    title={expanded ? '' : item.label}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon size={20} className="flex-shrink-0" />
                      {expanded && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </div>
                    {expanded && (
                      <ChevronDown
                        size={16}
                        className={cn(
                          'flex-shrink-0 transition-transform',
                          isExpanded && 'rotate-180'
                        )}
                      />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.to}
                    className={cn(
                      'flex items-center justify-center lg:justify-start gap-3 px-3 py-2 rounded-lg transition-colors text-xs'
                    )}
                    style={isActive ? {
                      color: 'rgb(248, 250, 252)',
                      backgroundColor: 'rgb(15, 23, 42)'
                    } : {
                      color: 'rgb(2, 8, 23)'
                    }}
                    title={expanded ? '' : item.label}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {expanded && (
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    )}
                  </Link>
                )}

                {/* Submenu - Solo visible cuando está expandido el sidebar y el menú */}
                {hasSubmenu && isExpanded && expanded && (
                  <ul className="mt-2 space-y-1 ml-2 border-l border-sidebar-border pl-3">
                    {item.submenu?.map((group) => (
                      <li key={group.group}>
                        <div className="mb-2">
                          <h4 className="text-xs font-bold text-sidebar-text-secondary uppercase tracking-wide">
                            {group.group}
                          </h4>
                          <p className="text-xs text-sidebar-text-secondary opacity-75">{group.description}</p>
                        </div>
                        <ul className="space-y-1 mb-3">
                          {group.items.map((subitem) => {
                            const SubIcon = subitem.icon;
                            const isSubActive = location.pathname === subitem.to;
                            return (
                              <li key={subitem.to}>
                                <Link
                                  to={subitem.to}
                                  className={cn(
                                    'flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                                    isSubActive
                                      ? 'bg-accent-blue/20 text-accent-blue font-medium'
                                      : 'text-sidebar-text-secondary hover:text-sidebar-text hover:bg-sidebar-hover/50'
                                  )}
                                >
                                  <SubIcon size={16} />
                                  <span>{subitem.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {/* Administració — visible per a tots els rols (2026-09-09), a
            diferencia de RRHH/Comptabilitat/Facturació/Documents que nomes
            veu el rol ADMIN (vegeu isAdmin/adminOnlyNavItems mes amunt). */}
        {true && (
          <>
            <div className="mx-3 my-4 border-t border-sidebar-border"></div>
            <div className={cn('px-3 mb-2', expanded && 'text-xs font-bold text-sidebar-text-secondary uppercase tracking-wide')}>
              {expanded ? 'Administración' : ''}
            </div>
            <ul className="space-y-1 px-3">
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to);

                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        'flex items-center justify-center lg:justify-start gap-3 px-3 py-2 rounded-lg transition-colors text-xs'
                      )}
                      style={isActive ? {
                        color: 'rgb(248, 250, 252)',
                        backgroundColor: 'rgb(15, 23, 42)'
                      } : {
                        color: 'rgb(2, 8, 23)'
                      }}
                      title={expanded ? '' : item.label}
                    >
                      <Icon size={20} className="flex-shrink-0" />
                      {expanded && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-sidebar-border p-3 relative" ref={userSectionRef}>
        {/* Popup */}
        {userPopupOpen && (
          <div className={cn(
            'absolute bg-white rounded-xl shadow-xl border border-gray-200 z-50',
            expanded
              ? 'bottom-full left-2 right-2 mb-2'
              : 'bottom-0 left-full ml-2 w-64'
          )}>
            {/* User info */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100 rounded-t-xl">
              <div className="h-9 w-9 rounded-full bg-gray-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {userInitials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            {/* Company selector */}
            {user?.companies && user.companies.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Empresa activa</p>
                {user.companies.length === 1 ? (
                  <p className="text-xs text-gray-900 font-medium">{currentCompanyName}</p>
                ) : (
                  <select
                    value={user.companyId ? String(user.companyId) : ''}
                    onChange={(e) => switchCompany(parseInt(e.target.value, 10))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {user.companies.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {/* Logout */}
            <div className="px-2 py-2">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Tancar sessió
              </button>
            </div>
          </div>
        )}

        {/* Trigger */}
        <button
          onClick={() => setUserPopupOpen(p => !p)}
          className={cn(
            'flex items-center gap-2.5 w-full rounded-lg px-2 py-2 transition-colors',
            'hover:bg-white/10',
            userPopupOpen && 'bg-white/10',
            !expanded && 'justify-center',
          )}
          title={expanded ? '' : `${user?.firstName} ${user?.lastName}`}
        >
          <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 ring-2 ring-white/20">
            {userInitials}
          </div>
          {expanded && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-xs font-semibold text-sidebar-text truncate leading-tight">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-sidebar-text-secondary truncate leading-tight">{currentCompanyName}</p>
              </div>
              <ChevronUp className={cn('h-3.5 w-3.5 text-sidebar-text-secondary flex-shrink-0 transition-transform duration-200', !userPopupOpen && 'rotate-180')} />
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
